import React, { Component, Fragment } from 'react';
import { Card } from 'bumbag';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _omitBy from 'lodash/omitBy';
import type { ReactElement } from 'react';
import Script from 'react-load-script';
import Loads from 'react-loads';
import { geocodeByAddress } from 'react-places-autocomplete';
import type { NavigateFunction } from 'react-router-dom';

import { WithErrorMessage } from '@medipass/react-core';
import { WithLoadingIndicator } from '@medipass/react-core';
import { PaymentMethod } from '@medipass/react-core/redux-form';
import { withRouter } from '@medipass/react-core/router';
import { isHealthPoint, isHICAPS, isMedicare, isOHC } from '@medipass/utils/funders';
import env from '@medipass/utils/get-env';
import { parseAddress } from '@medipass/utils/google-addresses';
import sdk from '@medipass/web-sdk';

import LocationPermissionsRejected from 'legacy/components/checkout/LocationPermissionsRejected';
import LocationPermissionsRequested from 'legacy/components/checkout/LocationPermissionsRequested';
import WithRequestLocationPermissions from 'legacy/components/WithRequestLocationPermissions';

import ApproveForm from './checkout/ApproveForm';
type Props = {
  children: ReactElement;
  navigate: NavigateFunction;
  onReject?: ((...args: Array<any>) => any) | null;
  transaction: Record<string, any>;
  isActive: boolean;
};
type State = {
  formData: Record<string, any> | null | undefined;
  isCreatingNewPaymentMethod: boolean;
  isRequestLocationEnabled: boolean;
  selectedPaymentMethod: Record<string, any> | null | undefined;
};

class WithApprove extends Component<Props, State> {
  static defaultProps = {
    onReject: null,
  };
  state = {
    formData: null,
    isCreatingNewPaymentMethod: false,
    isRequestLocationEnabled: false,
    selectedPaymentMethod: null,
  };
  // @ts-expect-error TS(7006): Parameter 'data' implicitly has an 'any' type.
  approve = async (data, position: any = {}) => {
    const { navigate, transaction } = this.props;
    const { selectedPaymentMethod } = this.state;
    const transactionId = transaction._id;
    const formData = data || this.state.formData || {};

    const paymentMethodId = _get(selectedPaymentMethod, '_id') as string | null | undefined;

    let approveRequestBody = {
      paymentMethodId,
    };

    if (formData.cardNumber && formData.expiry && formData.cvc) {
      const paymentMethod = await this.addPaymentMethod({
        cardNumber: formData.cardNumber,
        expiry: formData.expiry,
        cvc: formData.cvc,
      });
      approveRequestBody = {
        ...approveRequestBody,
        paymentMethodId: paymentMethod._id,
      };
    }

    if (position && position.coords) {
      approveRequestBody = {
        ...approveRequestBody,
        // @ts-expect-error TS(2322): Type '{ lnglat: any[]; paymentMethodId: any; }' is... Remove this comment to see the full error message
        lnglat: [position.coords.longitude, position.coords.latitude],
      };
    }

    if (data && _get(data, 'location.value')) {
      const results = await geocodeByAddress(_get(data, 'location.value'));
      const addressComponents = parseAddress(results[0].address_components);
      approveRequestBody = {
        ...approveRequestBody,
        // @ts-expect-error TS(2322): Type '{ postalAddress: any; paymentMethodId: any; ... Remove this comment to see the full error message
        postalAddress: _omitBy(addressComponents, _isEmpty),
      };
    }

    if (formData.bankAccountCheckBox === 'true') {
      approveRequestBody = {
        ...approveRequestBody,
        // @ts-expect-error TS(2322): Type '{ bankAccount: { accountName: any; accountNu... Remove this comment to see the full error message
        bankAccount: {
          accountName: data.bankAccountName,
          accountNumber: data.bankAccountNumber,
          bsb: data.bankBSB,
        },
      };
    }

    await sdk.transactions.approveTransaction(transactionId, approveRequestBody);
    navigate('/account/transactions?approveType=claim-and-payment');
  };
  addPaymentMethod = async ({ cardNumber, cvc, expiry }: { cardNumber: string; cvc: string; expiry: string }) => {
    const nonceResponse = await sdk.payments.getNonce({
      cardNumber,
      expiry,
      cvc,
      isSelfCheckoutSession: true,
    });

    const paymentMethod = await sdk.payments.createAccountPaymentMethod(
      _get(sdk, 'account._id'),
      (nonceResponse as any).nonce,
      (nonceResponse as any).source,
    );
    return paymentMethod;
  };
  fetchPaymentMethods = async () => {
    const accountId = _get(sdk, 'account._id');

    const paymentMethods = await sdk.payments.getAccountPaymentMethods(accountId);
    this.setState({
      selectedPaymentMethod: paymentMethods?.[0],
    });
    return paymentMethods;
  };
  handleChangePaymentMethod = (paymentMethod: Record<string, any>) => {
    if (_isEmpty(paymentMethod)) {
      this.setState({
        isCreatingNewPaymentMethod: true,
        selectedPaymentMethod: null,
      });
      return;
    }

    this.setState({
      isCreatingNewPaymentMethod: false,
      selectedPaymentMethod: paymentMethod,
    });
  };
  handleSubmit = async (data: Record<string, any>) => {
    const { transaction } = this.props;

    // If funder is HICAPS, we need to check for location.
    const funderCode = _get(transaction, 'claims[0].funder.code');
    if (isHICAPS(funderCode) || isOHC(funderCode) || isHealthPoint(funderCode)) {
      this.setState({ formData: data, isRequestLocationEnabled: true });
      return;
    }

    await this.approve(data);
  };
  handleReject = async () => {
    const { onReject, transaction } = this.props;
    // @ts-expect-error TS(2554): Expected 2-3 arguments, but got 1.
    await sdk.transactions.cancelTransaction(transaction._id);
    onReject && onReject();
  };
  render = () => {
    const { children, transaction, isActive } = this.props;
    const { isCreatingNewPaymentMethod, isRequestLocationEnabled, selectedPaymentMethod } = this.state;

    const funderCode = _get(transaction, 'claims[0].funder.code');

    return isActive ? (
      <Fragment>
        <Script url={env.HPF_SCRIPT_URL} />
        <Loads loadOnMount load={this.fetchPaymentMethods}>
          {/* @ts-expect-error TS(7031): Binding element 'paymentMethods' implicitly has an... Remove this comment to see the full error message */}
          {({ response: paymentMethods, isLoading, isSuccess }) => (
            <Loads load={this.approve}>
              {/* @ts-expect-error TS(7031): Binding element 'handleApprove' implicitly has an ... Remove this comment to see the full error message */}
              {({ load: handleApprove, error, isError, isLoading }) => (
                <WithLoadingIndicator isActive={isLoading}>
                  <WithErrorMessage
                    isActive={isError}
                    error={error}
                    description="Unable to approve transaction. Please refresh the page and try again."
                  >
                    <WithRequestLocationPermissions
                      enabled={isRequestLocationEnabled}
                      locationPermissionsRequestedRenderer={() => (
                        <LocationPermissionsRequested amountBenefit={transaction.amountBenefitString} />
                      )}
                      locationPermissionsRejectedRenderer={({ error }) => <LocationPermissionsRejected error={error} />}
                      onSuccess={position =>
                        handleApprove(null, {
                          position,
                        })
                      }
                    >
                      <ApproveForm
                        // @ts-expect-error
                        hasNoPaymentMethods={isSuccess ? paymentMethods.length === 0 : true}
                        isCheckout={false}
                        isCreatingNewPaymentMethod={isCreatingNewPaymentMethod}
                        isExistingAccount
                        onClickReject={this.handleReject}
                        onSubmit={this.handleSubmit}
                        showDeclaration={isHICAPS(funderCode) || isOHC(funderCode) || isHealthPoint(funderCode)}
                        showElectronicLodgementDeclaration={isMedicare(funderCode)}
                      >
                        {() => (
                          <Fragment>
                            {children}
                            <Card marginTop="major-2" marginBottom="0px">
                              <Fragment>
                                {isSuccess && (
                                  <PaymentMethod
                                    // @ts-expect-error
                                    onChangePaymentMethod={this.handleChangePaymentMethod}
                                    paymentMethods={paymentMethods}
                                    selectedPaymentMethod={selectedPaymentMethod}
                                  />
                                )}
                              </Fragment>
                            </Card>
                          </Fragment>
                        )}
                      </ApproveForm>
                    </WithRequestLocationPermissions>
                  </WithErrorMessage>
                </WithLoadingIndicator>
              )}
            </Loads>
          )}
        </Loads>
      </Fragment>
    ) : (
      children
    );
  };
}

export default withRouter(WithApprove);
